import type { ReactNode } from "react";

export type Tier =
  | "iron"
  | "bronze"
  | "silver"
  | "gold"
  | "platinum"
  | "diamond"
  | "grandmaster";

const KNOWN_TIERS: readonly Tier[] = [
  "iron",
  "bronze",
  "silver",
  "gold",
  "platinum",
  "diamond",
  "grandmaster",
];

const TIER_LABELS: Record<Tier, string> = {
  iron: "Iron",
  bronze: "Bronze",
  silver: "Silver",
  gold: "Gold",
  platinum: "Platinum",
  diamond: "Diamond",
  grandmaster: "Grandmaster",
};

const DIVISION_LABELS: Record<number, string> = {
  1: "I",
  2: "II",
  3: "III",
  4: "IV",
};

export type PlayerCardProps = {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  rating?: number | string | null;
  ratingDivision?: number | null;
  homeFacilityName?: string | null;
  relationship?: string | null;
  onClickIdentity?: () => void;
  leading?: ReactNode;
  actions?: ReactNode;
};

export interface PlayerCardIdentityDisplay {
  resolved: boolean;
  name: string;
  initials: string;
  handle: string | null;
}

/** A short discriminator, not a unique identifier or full-user-id display. */
export function playerHandleSuffix(userId: string): string {
  const id = (userId ?? "").trim();
  const last4 = id.length <= 4 ? id : id.slice(-4);
  return `#${last4.toLowerCase()}`;
}

export function toTier(tier: string): Tier | null {
  const slug = tier.trim().toLowerCase();
  return (KNOWN_TIERS as readonly string[]).includes(slug)
    ? (slug as Tier)
    : null;
}

export function tierDisplayLabel(tier: Tier, division?: number): string {
  const divisionLabel =
    tier !== "grandmaster" && division
      ? (DIVISION_LABELS[division] ?? String(division))
      : null;
  return divisionLabel
    ? `${TIER_LABELS[tier]} ${divisionLabel}`
    : TIER_LABELS[tier];
}

function isResolvedName(displayName: string, userId: string): boolean {
  const trimmed = displayName?.trim();
  return Boolean(trimmed && trimmed !== userId.trim());
}

function initials(displayName: string): string {
  const letters = displayName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .filter((character) => /\p{L}/u.test(character))
    .map((character) => character.toUpperCase())
    .join("");
  return letters || "?";
}

/** Privacy-safe projection used by audience-specific player renderers. */
export function playerCardIdentityDisplay(
  displayName: string,
  userId: string,
): PlayerCardIdentityDisplay {
  const resolved = isResolvedName(displayName, userId);
  const name = resolved ? displayName : "—";
  return {
    resolved,
    name,
    initials: initials(name),
    handle: resolved ? playerHandleSuffix(userId) : null,
  };
}
